// engagement-components/JourneyList.jsx
import React, { useState, useEffect } from "react";
import { Spinner } from "react-bootstrap";
import { FaEdit, FaCopy, FaTrash, FaPlay, FaPause, FaPlus, FaRoute } from "react-icons/fa";
import dayjs from "dayjs";
import axios from "axios";
import { apiUrl, authHeaders } from "./config";

const C = {
  brand: "#4f46e5", brandLt: "#eef2ff",
  green: "#059669", greenLt: "#ecfdf5",
  amber: "#d97706", amberLt: "#fffbeb",
  text: "#111827", muted: "#6b7280", subtle: "#9ca3af",
  border: "#e5e7eb", bg: "#f9fafb", white: "#ffffff",
  danger: "#ef4444",
};

const TRIGGER_META = {
  Birthday:           { emoji: "🎂", color: "#7c3aed", bg: "#f5f3ff" },
  Anniversary:        { emoji: "💍", color: "#be185d", bg: "#fff1f2" },
  "Check-in":         { emoji: "🏨", color: "#0369a1", bg: "#eff6ff" },
  "Check-out":        { emoji: "👋", color: "#0f766e", bg: "#f0fdfa" },
  "Profile Incomplete": { emoji: "👤", color: "#d97706", bg: "#fffbeb" },
  Manual:             { emoji: "⚡", color: "#6b7280", bg: "#f9fafb" },
  Custom:             { emoji: "⚡", color: "#6b7280", bg: "#f9fafb" },
};

const StatusPill = ({ status }) => {
  const map = {
    Active:  { bg: "#dcfce7", color: "#15803d" },
    Paused:  { bg: "#f3f4f6", color: "#6b7280" },
    Draft:   { bg: "#fef9c3", color: "#92400e" },
    Archived:{ bg: "#fee2e2", color: "#991b1b" },
  };
  const s = map[status] || map.Draft;
  return (
    <span style={{ fontSize:"0.68rem", fontWeight:700, padding:"3px 10px",
      borderRadius:20, background:s.bg, color:s.color, whiteSpace:"nowrap" }}>
      {status}
    </span>
  );
};

const JourneyList = ({ onNavigate }) => {
  const [journeys,    setJourneys]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [deleting,    setDeleting]    = useState(null);
  const [confirmId,   setConfirmId]   = useState(null);
  const [confirmName, setConfirmName] = useState("");

  useEffect(() => { fetchJourneys(); }, []);

  const fetchJourneys = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/feedback/journeys`, authHeaders());
      const normalized = (res.data || []).map((j) => ({
        ...j,
        id:      j.journeyid || j.id,
        steps:   j.steps_data?.length || 0,
        trigger: j.trigger_type || j.trigger,
      }));
      setJourneys(normalized);
    } catch {
      setJourneys([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (j) => {
    const next = j.status === "Active" ? "Paused" : "Active";
    const id   = j.journeyid || j.id;
    setJourneys((p) => p.map((x) => (x.journeyid || x.id) === id ? { ...x, status: next } : x));
    try {
      await axios.put(`${apiUrl}/feedback/journeys/${id}`,
        { name: j.name, trigger: j.trigger_type || j.trigger,
          triggerConditions: j.trigger_conditions, timezone: j.timezone,
          status: next, steps_data: j.steps_data || [] },
        authHeaders());
    } catch { fetchJourneys(); }
  };

  const duplicate = async (j) => {
    const copy = {
      name: `${j.name} (Copy)`,
      trigger: j.trigger_type || j.trigger,
      triggerConditions: j.trigger_conditions,
      timezone: j.timezone,
      status: "Draft",
      steps_data: j.steps_data || [],
    };
    try {
      const res = await axios.post(`${apiUrl}/feedback/journeys`, copy, authHeaders());
      const saved = res.data ? { ...res.data, id: res.data.journeyid } : { ...copy, id: `j${Date.now()}` };
      setJourneys((p) => [saved, ...p]);
    } catch {
      setJourneys((p) => [{ ...copy, id: `j${Date.now()}` }, ...p]);
    }
  };

  const deleteJourney = async () => {
    setDeleting(confirmId);
    try { await axios.delete(`${apiUrl}/feedback/journeys/${confirmId}`, authHeaders()); } catch {}
    setJourneys((p) => p.filter((x) => (x.journeyid || x.id) !== confirmId));
    setConfirmId(null); setDeleting(null);
  };

  if (loading) return (
    <div style={{ display:"flex", justifyContent:"center", alignItems:"center",
      minHeight:300 }}>
      <Spinner animation="border" style={{ color: C.brand }} />
    </div>
  );

  return (
    <div style={{ padding: "28px 32px" }}>

      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:28 }}>
        <div>
          <h2 style={{ fontSize:"1.45rem", fontWeight:800, color:C.text,
            letterSpacing:"-0.03em", margin:0 }}>
            Journeys
          </h2>
          <p style={{ fontSize:"0.84rem", color:C.muted, margin:"4px 0 0" }}>
            Automated, trigger-based guest communication sequences
          </p>
        </div>
        <button onClick={() => onNavigate("journeys-create")}
          style={{ display:"inline-flex", alignItems:"center", gap:8,
            padding:"10px 20px", borderRadius:10, border:"none",
            background:C.brand, color:C.white, fontSize:"0.84rem",
            fontWeight:700, cursor:"pointer",
            boxShadow:"0 4px 14px rgba(79,70,229,0.3)" }}>
          <FaPlus size={11} /> New Journey
        </button>
      </div>

      {/* Empty state */}
      {journeys.length === 0 && (
        <div style={{ textAlign:"center", padding:"72px 32px",
          background:C.white, borderRadius:16, border:`1px dashed ${C.border}` }}>
          <div style={{ width:64, height:64, borderRadius:16, background:C.brandLt,
            display:"flex", alignItems:"center", justifyContent:"center",
            margin:"0 auto 16px" }}>
            <FaRoute size={26} style={{ color:C.brand }} />
          </div>
          <div style={{ fontSize:"1rem", fontWeight:700, color:C.text, marginBottom:8 }}>
            No journeys yet
          </div>
          <p style={{ fontSize:"0.84rem", color:C.muted, marginBottom:20,
            maxWidth:360, margin:"0 auto 20px" }}>
            Create automated sequences that send birthday wishes, check-in greetings,
            post checkout feedback requests and more — on autopilot.
          </p>
          <button onClick={() => onNavigate("journeys-create")}
            style={{ padding:"10px 24px", borderRadius:10, border:"none",
              background:C.brand, color:C.white, fontSize:"0.84rem",
              fontWeight:700, cursor:"pointer" }}>
            <FaPlus size={11} style={{ marginRight:6 }} />
            Create Your First Journey
          </button>
        </div>
      )}

      {/* Journey grid — 3 columns */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3, 1fr)", gap:14 }}>
        {journeys.map((j) => {
          const meta   = TRIGGER_META[j.trigger] || TRIGGER_META.Custom;
          const id     = j.journeyid || j.id;
          const active = j.status === "Active";

          return (
            <div key={id} style={{
              background: C.white, borderRadius:12,
              border: `1px solid ${active ? "#c7d2fe" : C.border}`,
              borderTop: `3px solid ${active ? C.brand : C.border}`,
              padding:"16px", cursor:"pointer",
              transition:"all 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 18px rgba(0,0,0,0.09)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}>

              {/* Top row: emoji + status */}
              <div style={{ display:"flex", justifyContent:"space-between",
                alignItems:"flex-start", marginBottom:10 }}>
                <div style={{ width:38, height:38, borderRadius:10,
                  background:meta.bg, display:"flex", alignItems:"center",
                  justifyContent:"center", fontSize:"1.2rem", flexShrink:0 }}>
                  {meta.emoji}
                </div>
                <StatusPill status={j.status} />
              </div>

              {/* Name */}
              <div style={{ fontSize:"0.88rem", fontWeight:700, color:C.text,
                marginBottom:4, lineHeight:1.3 }}>
                {j.name}
              </div>

              {/* Trigger + steps */}
              <div style={{ fontSize:"0.74rem", color:C.muted, marginBottom:12,
                display:"flex", alignItems:"center", gap:6 }}>
                <span style={{ padding:"1px 7px", borderRadius:20, fontSize:"0.68rem",
                  fontWeight:600, background:meta.bg, color:meta.color }}>
                  {j.trigger}
                </span>
                <span>·</span>
                <span>{j.steps} step{j.steps !== 1 ? "s" : ""}</span>
              </div>

              {/* Stats */}
              <div style={{ display:"flex", gap:0,
                borderTop:`1px solid ${C.border}`, paddingTop:10, marginBottom:12 }}>
                {[
                  { label:"Fired",     val:(j.total_fired||0).toLocaleString(),     color:C.text  },
                  { label:"Delivered", val:(j.total_delivered||0).toLocaleString(), color:C.green },
                  { label:"Clicked",   val:(j.total_clicks||0).toLocaleString(),    color:C.brand },
                ].map(({ label, val, color }, i) => (
                  <div key={label} style={{ flex:1, textAlign:"center",
                    borderLeft: i > 0 ? `1px solid ${C.border}` : "none" }}>
                    <div style={{ fontSize:"1rem", fontWeight:800, color }}>{val}</div>
                    <div style={{ fontSize:"0.6rem", color:C.subtle,
                      textTransform:"uppercase", letterSpacing:"0.06em" }}>{label}</div>
                  </div>
                ))}
              </div>

              {/* Action buttons */}
              <div style={{ display:"flex", gap:6 }}
                onClick={e => e.stopPropagation()}>

                <button title="Edit"
                  onClick={() => onNavigate("journeys-edit", j)}
                  style={{ ...actionBtn(C.brand, "#eef2ff"), flex:1 }}>
                  <FaEdit size={12} />
                </button>

                <button title={active ? "Pause" : "Activate"}
                  onClick={() => toggleStatus(j)}
                  style={{ ...actionBtn(active ? C.amber : C.green, active ? C.amberLt : C.greenLt), flex:1 }}>
                  {active ? <FaPause size={12} /> : <FaPlay size={12} />}
                </button>

                <button title="Duplicate"
                  onClick={() => duplicate(j)}
                  style={{ ...actionBtn("#0891b2", "#ecfeff"), flex:1 }}>
                  <FaCopy size={12} />
                </button>

                <button title="Delete"
                  onClick={() => { setConfirmId(id); setConfirmName(j.name); }}
                  style={{ ...actionBtn(C.danger, "#fef2f2"), flex:1 }}>
                  <FaTrash size={12} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Delete confirm overlay */}
      {confirmId && (
        <div style={{ position:"fixed", inset:0, zIndex:1060,
          display:"flex", alignItems:"center", justifyContent:"center",
          background:"rgba(0,0,0,0.4)" }}>
          <div style={{ background:C.white, borderRadius:16, padding:"28px 32px",
            width:380, boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ fontSize:"1rem", fontWeight:800, color:C.text, marginBottom:8 }}>
              Delete Journey?
            </div>
            <p style={{ fontSize:"0.84rem", color:C.muted, marginBottom:24 }}>
              <strong>{confirmName}</strong> will be permanently deleted.
              All scheduled messages will stop firing.
            </p>
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button onClick={() => setConfirmId(null)}
                style={{ padding:"8px 20px", borderRadius:8, border:`1px solid ${C.border}`,
                  background:C.white, color:C.text, fontSize:"0.84rem",
                  fontWeight:600, cursor:"pointer" }}>
                Cancel
              </button>
              <button onClick={deleteJourney} disabled={!!deleting}
                style={{ padding:"8px 20px", borderRadius:8, border:"none",
                  background:C.danger, color:C.white, fontSize:"0.84rem",
                  fontWeight:700, cursor:"pointer" }}>
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const actionBtn = (color, bg) => ({
  width:32, height:32, borderRadius:8, border:"none",
  background:bg, color:color, cursor:"pointer",
  display:"flex", alignItems:"center", justifyContent:"center",
  transition:"opacity 0.12s",
});

export default JourneyList;
